import debounce from 'lodash/debounce';
// import { v4 as uuidv4 } from 'uuid';

import { getFeature } from './config';

import type { I13n, FormattedPromotion, BannerPromotion } from '../types';

const rudderstackUrl = getFeature('rudderstackUrl');
const bufferedRudderstackKey = getFeature('rudderstackWriteKey');

const impressionSendRate = 0.1;

// const uuid = uuidv4();
const uuid = String(Date.now());

// If we don't want to track Rudderstack for a property, add the property ID here
const skipPropertyAnalyticsById = {};

const debounceWithQueue = (fn: (queue: Array<any>) => void, wait: number, maxLength = 100) => {
    const queue = [];

    const runFn = () => {
        fn(queue);
        queue.length = 0;
    };

    const debounced = debounce(runFn, wait);

    const cancel = () => {
        debounced.cancel();
        queue.length = 0;
    };

    const queuedDebounce = (...a) => {
        queue.push(a);

        if (queue.length >= maxLength) {
            debounced.flush();
        } else {
            debounced();
        }
    };

    queuedDebounce.cancel = cancel;

    queuedDebounce.flush = debounced.flush;

    return queuedDebounce;
};

export const debouncedLogEvent = debounceWithQueue(handlelogEvent, 500);

function handlelogEvent(queue: Array<[number, I13n]>) {
    const formattedQueue: Array<I13n> = queue
        .map(([propertyId, i13n]) => {
            if (!skipPropertyAnalyticsById[propertyId]) {
                return i13n;
            }
        })
        .filter(Boolean);

    if (formattedQueue.length === 0) {
        return;
    }

    if (formattedQueue.length > 1) {
        batchLogEvent(formattedQueue);
        return;
    }

    logEvent(formattedQueue[0]);
}

type EventPayload = {
    type: string;
    properties: I13n;
    event: string;
    anonymousId: string;
};

type BatchEventPayload = {
    event: 'BAM Batched Events';
    type: 'track';
    properties: {
        events: Array<EventPayload>;
    };
    anonymousId: string;
};

function logEvent(i13n: I13n) {
    if (i13n.action === 'BAM Promo Impression' && Math.random() > impressionSendRate) {
        return;
    }

    if (i13n.action === 'BAM Promo Impression') {
        i13n.impression_send_rate = impressionSendRate;
    }
    const payload: EventPayload = {
        anonymousId: uuid,
        type: 'track',
        properties: i13n,
        event: i13n.action,
    };

    fetchEvent({ type: 'track', payload });
}

function batchLogEvent(queue: Array<I13n>) {
    const events = queue
        .map(i13n => {
            if (i13n.action === 'BAM Promo Impression' && Math.random() > impressionSendRate) {
                return;
            }

            if (i13n.action === 'BAM Promo Impression') {
                i13n.impression_send_rate = impressionSendRate;
            }
            return {
                anonymousId: uuid,
                event: i13n.action,
                type: 'track',
                properties: i13n,
            };
        })
        .filter(Boolean);

    if (events.length > 0) {
        const payload: Array<BatchEventPayload> = [
            {
                anonymousId: uuid,
                event: 'BAM Batched Events',
                type: 'track',
                properties: {
                    events: events,
                },
            },
        ];

        fetchEvent({ type: 'batch', payload });
    }
}

async function fetchEvent({
    type,
    payload,
}: { type: 'track'; payload: EventPayload } | { type: 'batch'; payload: Array<BatchEventPayload> }) {
    await fetch(`${rudderstackUrl}/${type}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Basic ${bufferedRudderstackKey}`,
        },
        body: JSON.stringify(Array.isArray(payload) ? { batch: payload } : payload),
    });
}

export type ComponentType =
    | 'Promotion List'
    | 'Sticky CTA'
    | 'Sticky Image CTA'
    | 'Banner'
    | 'Rotating Banner'
    | 'Inline Promotion'
    | 'Bonus Inline Promotion'
    | 'Inline Promotion Block'
    | 'Image'
    | 'Casino Banner'
    | 'Email Capture'
    | 'Recirc Logo'
    | 'Trending Parlays'
    | 'Promo Overlay';

export type PromotionOptions = {
    stateCode: string | null;
    context: string;
    propertyId: number;
    placementId: number | null;
    league: string | null;
    allPromotions: Array<FormattedPromotion | BannerPromotion>;
    pageSlug?: string | null;
};

type PromotionEventProps = {
    componentType: ComponentType;
    action: 'Click' | 'Impression';
    promotion: FormattedPromotion | BannerPromotion;
    options: PromotionOptions;
};

type DataEventProps = {
    componentType: ComponentType;
    action: 'Click' | 'Impression';
    options: PromotionOptions;
};

export function getPromotionEvent({ componentType, action, promotion, options }: PromotionEventProps): I13n {
    const { id, bookId, campaignId, promotionListId } = promotion;

    const { stateCode, context, propertyId, placementId, league, allPromotions, pageSlug } = options;

    const i13n = {
        action: `BAM Promo ${action}`,
        category: `BAM ${componentType}`,
        url: window.location.href,
        label: bookId,
        bam_property_id: propertyId,
        bam_placement_id: placementId,
        bam_promotion_id: id,
        bam_campaign_id: campaignId,
        bam_affiliate_id: bookId,
        bam_state_code: stateCode || null,
        bam_context: context,
        bam_promotion_list_id: promotionListId,
        bam_list_position: allPromotions.indexOf(promotion) + 1,
        bam_league_id: league || null,
        bam_list_length: allPromotions.length,
        bam_page_slug: pageSlug || null,
        bam_list_contents: allPromotions.map(promo => promo.id),
    };

    return i13n;
}

export function getDataEvent({ componentType, action, options }: DataEventProps): I13n {
    const { propertyId, league, pageSlug } = options;

    const i13n = {
        action: `BAM Data ${action}`,
        category: `BAM ${componentType}`,
        url: window.location.href,
        bam_property_id: propertyId,
        bam_page_slug: pageSlug || null,
        bam_league_id: league || null,
    };

    return i13n;
}

export function logEmailCapture(i13n: I13n) {
    if (i13n.action === 'BAM Email Capture' && i13n.label === 'email') {
        const payload: EventPayload = {
            anonymousId: uuid,
            type: 'emailCapture',
            properties: i13n,
            event: i13n.action,
        };

        fetchEvent({ type: 'track', payload });
        return true;
    }

    return false;
}
